import { useMemo, useState } from 'react'
import type { Book } from './lib/book'
import { compare, defaultBaseline, recChanges, type Format, type Measure } from './lib/changes'
import { CLASS_LABEL } from './charts'

const whole = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 })

function show(v: number | null, f: Format): string {
  if (v === null) return '—'
  if (f === 'money') return whole.format(v)
  if (f === 'percent') return `${Math.round(v * 100)}%`
  if (f === 'months') return `${v} mo`
  return String(v)
}

/** Arrow and size of the change; "better"/"worse" in words, so nothing rests on colour. */
function Delta({ m }: { m: Measure }) {
  if (m.then === null || m.now === null) return <span className="muted">—</span>
  const d = m.now - m.then
  if (Math.abs(d) < (m.format === 'percent' ? 0.0005 : 0.5)) return <span className="muted">no change</span>
  const size = m.format === 'percent' ? `${Math.abs(Math.round(d * 1000) / 10)} pts` : show(Math.abs(Math.round(d * 10) / 10), m.format)
  const better = d > 0 === m.higherIsBetter
  return (
    <span className={better ? 'delta' : 'delta worse'}>
      {d > 0 ? '▲' : '▼'} {size} <span className="label">{better ? 'better' : 'worse'}</span>
    </span>
  )
}

export function WhatChanged({ book }: { book: Book }) {
  const runs = book.analyses.map((a) => String(a.run_at ?? '')).filter(Boolean)
  const [chosen, setChosen] = useState<string>()
  const baseline = chosen ?? defaultBaseline(book.tables, book.analyses)
  const c = useMemo(() => (baseline ? compare(book.tables, book.snapshot, baseline) : undefined), [book, baseline])
  const recs = useMemo(() => recChanges(book.analyses), [book.analyses])
  if (!baseline || !c) return null

  return (
    <section className="section">
      <div className="row">
        <h2>What changed</h2>
        <label className="inline">
          <span className="label">Since the review of</span>{' '}
          <select value={baseline} onChange={(e) => setChosen(e.target.value)}>
            {[...runs].reverse().map((r) => (
              <option key={r} value={r}>{r.replace('T', ' ').replace('Z', ' UTC')}</option>
            ))}
          </select>
        </label>
      </div>
      {c.documents.length === 0 ? (
        <p className="muted">No statements have been read since that review, so the figures are the same. Pick an earlier review to compare.</p>
      ) : (
        <p className="muted">
          {c.documents.length} document{c.documents.length === 1 ? '' : 's'} read since:{' '}
          {c.documents.map((d) => String(d.name ?? '').replace(/\.[a-z]+$/i, '')).join(' · ')}
        </p>
      )}

      <div className="grid">
        <table className="table">
          <thead><tr><th>Measure</th><th className="r">Then</th><th className="r">Now</th><th className="r">Change</th></tr></thead>
          <tbody>
            {c.measures.map((m) => (
              <tr key={m.label}>
                <td>{m.label}</td>
                <td className="r num">{show(m.then, m.format)}</td>
                <td className="r num">{show(m.now, m.format)}</td>
                <td className="r num"><Delta m={m} /></td>
              </tr>
            ))}
          </tbody>
        </table>
        <div>
          <table className="table">
            <thead><tr><th>Account group</th><th className="r">Then</th><th className="r">Now</th><th className="r">Change</th></tr></thead>
            <tbody>
              {c.groups.map((m) => (
                <tr key={m.label}>
                  <td>{m.label}</td>
                  <td className="r num">{show(m.then, m.format)}</td>
                  <td className="r num">{show(m.now, m.format)}</td>
                  <td className="r num"><Delta m={m} /></td>
                </tr>
              ))}
            </tbody>
          </table>
          {c.allocation.length > 0 && (
            <>
              <h3 className="subhead">Allocation shifts</h3>
              <ul className="plain">
                {c.allocation.map((a) => (
                  <li key={a.label}>
                    {CLASS_LABEL[a.label] ?? a.label}: <span className="num">{Math.round(a.then * 100)}% → {Math.round(a.now * 100)}%</span>{' '}
                    <span className="muted">({a.now > a.then ? '▲' : '▼'} {Math.abs(Math.round((a.now - a.then) * 1000) / 10)} pts)</span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      </div>

      <h3 className="subhead">Recommendations in the latest review</h3>
      {recs ? (
        <ul className="plain">
          {recs.added.map((r) => <li key={`a${r.title}`}><span className="label">+ New</span> {r.title}</li>)}
          {recs.kept.map((r) => <li key={`k${r.title}`}><span className="label">= Carried over</span> {r.title}</li>)}
          {recs.dropped.map((r) => <li key={`d${r.title}`} className="muted"><span className="label">− No longer made</span> {r.title}</li>)}
        </ul>
      ) : (
        <p className="muted small">Tracked from the next two reviews on: each review now records its recommendations so the next can be compared with it.</p>
      )}
    </section>
  )
}
